import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useAddNewSiteAddressToProject from '@/api/mutations/project/useAddNewSiteAddressToProject';
import complianceKeys from '@/api/queries/compliance/keyFactory';
import projectKeys from '@/api/queries/project/keyFactory';
import {
  AddNewSiteAddressToProjectMutation,
  AddNewSiteAddressToProjectMutationVariables,
  ProjectByIdQuery,
} from '@/gql/graphql';

const useOptimisticAddNewSiteAddressToProject = (
  projectId: string,
  options?: UseMutationOptions<
    AddNewSiteAddressToProjectMutation,
    unknown,
    {
      unlinkSiteAddressFromProjectInput?: AddNewSiteAddressToProjectMutationVariables['unlinkSiteAddressFromProjectInput'];
      addNewSiteAddressToProjectInput: AddNewSiteAddressToProjectMutationVariables['addNewSiteAddressToProjectInput'];
    }
  >
) => {
  const queryClient = useQueryClient();

  return useAddNewSiteAddressToProject({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: projectKeys.overview(projectId),
      });

      // Snapshot the previous value
      const previousProjectOverview = queryClient.getQueryData(
        projectKeys.overview(projectId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<ProjectByIdQuery>(
        projectKeys.overview(projectId),
        (old) => {
          if (!old) return;

          const newAddress = {
            ...variables.addNewSiteAddressToProjectInput.address,
            id: 'temp-id',
            publicId: 'temp-public-id',
            fullLocalAddress: [
              variables.addNewSiteAddressToProjectInput.address.line1,
              variables.addNewSiteAddressToProjectInput.address.line2,
              variables.addNewSiteAddressToProjectInput.address.city,
              variables.addNewSiteAddressToProjectInput.address
                .administrativeArea,
              variables.addNewSiteAddressToProjectInput.address.postalCode,
              variables.addNewSiteAddressToProjectInput.address.country,
            ].join(', '),
            coordinatesSource: 'LOOKUP' as const,
            coordinates: null,
            contacts: [],
            energyPerformanceCertificates: [],
          };

          return {
            ...old,
            projectById: {
              ...old.projectById,
              addresses: {
                ...old.projectById.addresses,
                site: [
                  ...old.projectById.addresses.site.filter(
                    (site) =>
                      site.id !==
                      variables.unlinkSiteAddressFromProjectInput?.addressId
                  ),
                  newAddress,
                ],
              },
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousProjectOverview };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        projectKeys.overview(projectId),
        // @ts-expect-error
        context.previousProjectOverview
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.overview(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: complianceKeys.formPrefill(projectId),
      });
      // The site address drives the EPC-derived heat loss estimate, which in
      // turn determines whether existing Estimates are out of date.
      void queryClient.invalidateQueries({
        queryKey: projectKeys.heatLossEstimate(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.estimates(projectId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticAddNewSiteAddressToProject;
